// Mapeamento entre a IDENTIDADE LOCAL (local-db) e a do SERVIDOR (sync backend).
// Plan: docs/plan/sync-conta-cross-device.md (S3.1).
//
// No local-db cada item tem id = `${scope}::${rawId}` onde:
//   scope  = `p:CODE::pf:PROFILEID`  (ou `d:LIST::pf:ID` p/ lista direta)
//   rawId  = a identidade ESTÁVEL do conteúdo (ex: `vod:123`, `series:55`,
//            `series-episode:55:990`, `channel:abc`, `series-progress:55`)
//
// No servidor (swipe_sync_items) o escopo já são COLUNAS (provider_code via
// perfil, profile_id) e o `item_key` = SÓ o rawId (a identidade do conteúdo).
// Então sync = decompor o id local em (providerCode, profileId, item_key).
//
// REGRA: só sincroniza login com CÓDIGO de provedor (`p:CODE`) — listas diretas
// (`d:LIST`, M3U sem conta) ficam 100% locais. E só com perfil REAL (não `_main`).

export interface ScopeParts {
  providerCode: string
  profileId: string
}

/**
 * Decompõe o `scope` local (`p:CODE::pf:PROFILEID`) em (providerCode, profileId).
 * Retorna null se NÃO for sincronizável: lista direta (`d:`), perfil `_main`
 * (login sem perfil escolhido), ou formato inesperado.
 */
export function parseSyncScope(scope: string): ScopeParts | null {
  // scope = `${providerPart}::${profilePart}` = `p:CODE::pf:ID`
  const sep = scope.indexOf('::')
  if (sep < 0) return null
  const providerPart = scope.slice(0, sep)
  const profilePart = scope.slice(sep + 2)

  if (!providerPart.startsWith('p:')) return null // só código de provedor (não `d:`)
  const providerCode = providerPart.slice(2)
  if (!providerCode) return null

  if (!profilePart.startsWith('pf:')) return null
  const profileId = profilePart.slice(3)
  if (!profileId || profileId === '_main') return null // perfil real obrigatório

  return { providerCode, profileId }
}

export interface DecomposedItem extends ScopeParts {
  itemKey: string // o rawId (identidade estável do conteúdo)
}

/**
 * Decompõe um id LOCAL completo (`p:CODE::pf:ID::rawId`) em
 * (providerCode, profileId, itemKey). null se não sincronizável.
 * O `rawId` pode conter `::`? Não — as keys do local-db usam `:` simples como
 * separador interno (`vod:123`, `series-episode:55:990`), e o `::` só aparece
 * entre scope e rawId. Pegamos tudo após o SEGUNDO `::`.
 */
export function decomposeLocalId(localId: string): DecomposedItem | null {
  const first = localId.indexOf('::')
  if (first < 0) return null
  const second = localId.indexOf('::', first + 2)
  if (second < 0) return null
  const scope = localId.slice(0, second)
  const itemKey = localId.slice(second + 2)
  if (!itemKey) return null
  const parts = parseSyncScope(scope)
  if (!parts) return null
  return { ...parts, itemKey }
}

/** Recompõe o id local a partir das partes (pro puller gravar no local-db). */
export function composeLocalId(providerCode: string, profileId: string, itemKey: string): string {
  return `p:${providerCode}::pf:${profileId}::${itemKey}`
}
